import { cn } from '@shared/lib/cn'
import { Button } from '@shared/ui/button'
import { HeroLayout } from '@shared/ui/hero-layout'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import {
  HiMiniAcademicCap,
  HiMiniBanknotes,
  HiMiniBriefcase,
  HiMiniHeart,
  HiMiniPlus,
  HiMiniShieldCheck,
  HiMiniUserGroup,
} from 'react-icons/hi2'

const CONCERN_OPTIONS = [
  { icon: HiMiniBriefcase, label: '직장' },
  { icon: HiMiniHeart, label: '연애' },
  { icon: HiMiniBanknotes, label: '재물' },
  { icon: HiMiniShieldCheck, label: '건강' },
  { icon: HiMiniAcademicCap, label: '학업' },
  { icon: HiMiniUserGroup, label: '대인 관계' },
] as const

interface Step4ConcernsProps {
  readonly name: string
  readonly concerns: string[]
  readonly concernText: string
  readonly onConcernsChange: (v: string[]) => void
  readonly onConcernTextChange: (v: string) => void
  readonly onBack: () => void
  readonly onNext: () => void
}

export function Step4Concerns({
  name,
  concerns,
  concernText,
  onConcernsChange,
  onConcernTextChange,
  onBack,
  onNext,
}: Step4ConcernsProps) {
  const [showTextInput, setShowTextInput] = useState(false)
  const hasSelection = showTextInput ? concernText.trim().length > 0 : concerns.length > 0

  return (
    <HeroLayout
      characterImage="/character-assets/3_form_cleaned.png"
      progressStep={4}
      title="요즘 고민이 있으세요?"
      subtitle="맞춤 풀이를 준비할게요"
      onBack={onBack}
      footer={
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="flex-1"
            onClick={() => {
              onConcernsChange([])
              onConcernTextChange('')
              onNext()
            }}
          >
            괜찮아요
          </Button>
          <Button
            type="button"
            variant="primary"
            size="lg"
            className="flex-[2]"
            disabled={!hasSelection}
            onClick={onNext}
          >
            다음
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        {/* 고민 칩 + 직접 입력 뱃지 */}
        <div className="flex flex-wrap gap-2">
          {CONCERN_OPTIONS.map((opt) => {
            const Icon = opt.icon
            const isSelected = concerns.includes(opt.label)
            return (
              <button
                key={opt.label}
                type="button"
                className={cn(
                  'flex cursor-pointer items-center gap-1.5 rounded-full px-4 py-2.5 typo-label font-semibold transition-all',
                  isSelected
                    ? 'border border-primary bg-primary/10 text-foreground'
                    : 'border border-border bg-bg-surface text-muted-foreground',
                )}
                onClick={() => {
                  if (showTextInput) {
                    setShowTextInput(false)
                  }
                  if (isSelected) {
                    onConcernsChange(concerns.filter((c) => c !== opt.label))
                  } else {
                    onConcernsChange([...concerns, opt.label])
                  }
                }}
              >
                <Icon
                  className={cn('text-base', isSelected ? 'text-primary' : 'text-muted-foreground')}
                />
                {opt.label}
              </button>
            )
          })}

          {/* 직접 입력 뱃지 */}
          <button
            type="button"
            className={cn(
              'flex cursor-pointer items-center gap-1.5 rounded-full px-4 py-2.5 typo-label font-semibold transition-all',
              showTextInput
                ? 'border border-primary bg-primary/10 text-foreground'
                : 'border border-border bg-bg-surface text-foreground',
            )}
            onClick={() => {
              setShowTextInput(!showTextInput)
            }}
          >
            <HiMiniPlus
              className={cn('text-base', showTextInput ? 'text-primary' : 'text-foreground')}
            />
            직접 입력
          </button>
        </div>

        {/* textarea 펼침 */}
        <AnimatePresence>
          {showTextInput && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              className="overflow-hidden"
            >
              <div className="relative">
                <textarea
                  placeholder="편하게 적어주세요, 어떤 이야기든 좋아요"
                  maxLength={150}
                  className="min-h-[100px] w-full resize-none rounded-xl border border-border bg-bg-surface px-4 py-3.5 pb-8 font-sans typo-body text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
                  value={concernText}
                  onChange={(e) => {
                    onConcernTextChange(e.target.value)
                  }}
                />
                <span className="absolute bottom-3.5 right-3.5 typo-footnote text-muted-foreground/50">
                  {concernText.length}/150
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </HeroLayout>
  )
}
